import {
  Autocomplete,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { formatDate } from 'lib/functions/utils';
import React, { useCallback, useEffect, useState } from 'react';

import { getAutocompleteGlobalSearcher } from '../../api/search';
import CustomButton from '../../designSystem/Button';
import MenuDialog from '../../designSystem/MenuDialog';
import { datesShortCuts } from '../../functions/const/datesShortCuts'; // Atajos de fechas

dayjs.extend(utc);

export interface Filter {
  type: 'date' | 'text' | 'select' | 'multiple' | 'autocomplete';
  label: string;
  options?: { value: string; label: string }[]; // For select type
  value?: any;
  setter: (value: any) => void;
  specialHandler?: string;
  dateKey?: string;
  key?: string;
  // For autocomplete type
  selectedValue?: { value: string; label: string } | null;
  setSelectedValue?: (value: { value: string; label: string } | null) => void;
}

interface SearchComponentProps {
  filters: Filter[];
  triggerSearch: (params?: any) => Promise<any>;
  pagination?: { pageSize: number };
  sortOptions?: { value: string; label: string }[];
  selectedSortBy?: string;
  setSelectedSortBy?: (key: string) => void;
  sortOrder?: 'asc' | 'desc';
  setSortOrder?: (order: 'asc' | 'desc') => void;
  dispatch?: any; // Para el requestHandler
  disableSearch?: boolean;
}

const GlobalSearcher: React.FC<SearchComponentProps> = ({
  filters,
  triggerSearch,
  selectedSortBy,
  setSelectedSortBy,
  sortOptions,
  sortOrder,
  setSortOrder,
  dispatch,
  disableSearch,
}) => {
  const [useShortcuts, setUseShortcuts] = useState(false);
  const [shortcut, setShortcut] = useState('');
  const [fakeValues, setFakeValues] = useState<{ [key: string]: any }>({});
  const [autocompleteOptions, setAutocompleteOptions] = useState<{
    [key: string]: { value: string; label: string }[];
  }>({});
  const [autocompleteLoading, setAutocompleteLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [autocompleteInputValues, setAutocompleteInputValues] = useState<{
    [key: string]: string;
  }>({});
  const [autocompleteValues, setAutocompleteValues] = useState<{
    [key: string]: { value: string; label: string } | null;
  }>({});
  const [debounceTimers, setDebounceTimers] = useState<{
    [key: string]: NodeJS.Timeout;
  }>({});
  console.log(filters);

  // Función interna para obtener opciones del autocomplete
  const getAutocompleteOptions = async (
    label: string,
    input: string,
  ): Promise<{ value: string; label: string }[]> => {
    if (!dispatch) {
      console.error('Dispatch is required for autocomplete functionality');
      return [];
    }

    try {
      const response = await getAutocompleteGlobalSearcher(dispatch, {
        label,
        input,
      });
      return response || [];
    } catch (error) {
      console.error('Error fetching autocomplete options:', error);
      return [];
    }
  };

  const handleSearch = async () => {
    if (!dateValidations()) {
      return;
    }
    await triggerSearch({});
  };

  const dateValidations = () => {
    const startDateFilter = filters.find((f) => f.dateKey === 'startDate');
    const endDateFilter = filters.find((f) => f.dateKey === 'endDate');
    if (startDateFilter?.value && !endDateFilter?.value) {
      alert('Si agregas una fecha de inicio, debes agregar una fecha de fin');
      return false;
    }
    if (new Date(startDateFilter?.value) > new Date(endDateFilter?.value)) {
      alert('La fecha de inicio no puede ser mayor a la fecha de fin');
      return false;
    }
    return true;
  };

  const handleChangeDateNormal = (value: any, type: string) => {
    if (type === 'start') {
      const startSetter = filters.find(
        (f) => f.dateKey === 'startDate',
      )?.setter;
      if (!startSetter) {
        console.error('No se encontró el setter para la fecha de inicio');
        return;
      }
      startSetter(value ? formatDate(value) : null);
    } else {
      const endSetter = filters.find((f) => f.dateKey === 'endDate')?.setter;
      if (!endSetter) {
        console.error('No se encontró el setter para la fecha de fin');
        return;
      }
      endSetter(value ? formatDate(value) : null);
    }
  };

  const handleChangeShortcut = (shortcut: string) => {
    if (shortcut === 'none') {
      setShortcut(shortcut);
      filters.forEach((f) => {
        if (f.dateKey === 'startDate') {
          f.setter(null);
        }
        if (f.dateKey === 'endDate') {
          f.setter(null);
        }
      });
      return;
    }
    const startSetter = filters.find((f) => f.dateKey === 'startDate')?.setter;
    const endSetter = filters.find((f) => f.dateKey === 'endDate')?.setter;
    if (!startSetter || !endSetter) {
      console.error('No se encontró el setter para las fechas');
      return;
    }
    const selectedShortcut = datesShortCuts.find((sc) => sc.label === shortcut);
    if (selectedShortcut) {
      startSetter(dayjs(selectedShortcut.startDate).startOf('day').utc());
      endSetter(dayjs(selectedShortcut.endDate).endOf('day').utc());
      setShortcut(shortcut);
    }
  };

  const handleChangeText = (
    value: any,
    setter: (value: any) => void,
    label: string,
    specialHandler?: string,
  ) => {
    if (specialHandler === 'id') {
      setFakeValues({
        ...fakeValues,
        [label]: value,
      });
      setter(value.replace('P', '').replace('p', ''));
    } else {
      setFakeValues({ ...fakeValues, [label]: value });
      setter(value);
    }
  };

  const handleAutocompleteInputChange = useCallback(
    (label: string, inputValue: string, reason: string) => {
      const filter = filters.find((f) => f.label === label);
      if (!filter || filter.type !== 'autocomplete' || !filter.key) return;

      // Always update the input value
      setAutocompleteInputValues((prev) => ({
        ...prev,
        [label]: inputValue,
      }));

      // Only search if the user is typing
      if (reason === 'input') {
        // Clear previous timer
        if (debounceTimers[label]) {
          clearTimeout(debounceTimers[label]);
        }

        // Set loading state
        setAutocompleteLoading((prev) => ({
          ...prev,
          [label]: true,
        }));

        // Create new timer
        const timer = setTimeout(async () => {
          try {
            if (inputValue.trim()) {
              if (!filter.key) return;
              const options = await getAutocompleteOptions(
                filter.key,
                inputValue,
              );
              setAutocompleteOptions((prev) => ({
                ...prev,
                [label]: options,
              }));
            } else {
              setAutocompleteOptions((prev) => ({
                ...prev,
                [label]: [],
              }));
            }
          } catch (error) {
            console.error('Error searching autocomplete options:', error);
            setAutocompleteOptions((prev) => ({
              ...prev,
              [label]: [],
            }));
          } finally {
            setAutocompleteLoading((prev) => ({
              ...prev,
              [label]: false,
            }));
          }
        }, 1000);

        setDebounceTimers((prev) => ({
          ...prev,
          [label]: timer,
        }));
      }
    },
    [filters, debounceTimers, dispatch],
  );

  const handleAutocompleteChange = (
    label: string,
    value: { value: string; label: string } | null,
  ) => {
    const filter = filters.find((f) => f.label === label);
    if (!filter || filter.type !== 'autocomplete') return;

    // Update internal state immediately
    setAutocompleteValues((prev) => ({
      ...prev,
      [label]: value,
    }));

    // Update the main setter immediately
    if (filter.setter) {
      filter.setter(value?.value || null);
    }

    // Update the selected value
    if (filter.setSelectedValue) {
      filter.setSelectedValue(value);
    }

    // Update input value to show the selected option or clear it
    setAutocompleteInputValues((prev) => ({
      ...prev,
      [label]: value ? value.label : '',
    }));

    // Update fake values for display
    setFakeValues((prev) => ({
      ...prev,
      [label]: value?.label || '',
    }));
  };

  const setUpFakeValues = () => {
    const newFakeValues: { [key: string]: any } = {};
    filters.forEach((filter) => {
      if (filter.value) {
        if (filter.specialHandler === 'id') {
          const value = `P${filter.value}`;
          newFakeValues[filter.label] = value;
        } else {
          newFakeValues[filter.label] = filter.value;
        }
      }
    });
    setFakeValues(newFakeValues);
  };

  useEffect(() => {
    setUpFakeValues();
  }, []);

  // Initialize autocomplete values
  useEffect(() => {
    const newInputValues: { [key: string]: string } = {};
    const newAutocompleteValues: {
      [key: string]: { value: string; label: string } | null;
    } = {};

    filters.forEach((filter) => {
      if (filter.type === 'autocomplete') {
        if (filter.selectedValue) {
          newInputValues[filter.label] = filter.selectedValue.label;
          newAutocompleteValues[filter.label] = filter.selectedValue;
        } else if (filter.value) {
          // If we have a value but no selectedValue, create one
          newAutocompleteValues[filter.label] = {
            value: filter.value,
            label: filter.value,
          };
          newInputValues[filter.label] = filter.value;
        }
      }
    });

    setAutocompleteInputValues(newInputValues);
    setAutocompleteValues(newAutocompleteValues);
  }, [filters]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [debounceTimers]);

  return (
    <MenuDialog buttonLabel="Buscar" buttonColor="secondary">
      <div
        style={{
          padding: '8px 16px',
          maxHeight: 600,
          overflowY: 'auto',
          maxWidth: 300,
        }}
      >
        <p style={{ fontSize: '1px', color: 'transparent' }}>_</p>
        {filters.some((f) => f.dateKey) && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="bold" marginBottom={1}>
                Fechas
              </Typography>
              <Typography
                variant="body1"
                fontWeight="bold"
                onClick={() => setUseShortcuts(!useShortcuts)}
                style={{ cursor: 'pointer', color: 'blue' }}
              >
                {useShortcuts ? 'Ocultar' : 'Mostrar'} atajos
              </Typography>
            </div>
            {useShortcuts ? (
              <FormControl fullWidth>
                <InputLabel id="shortcut-label">Atajo</InputLabel>
                <Select
                  labelId="shortcut-label"
                  value={shortcut}
                  onChange={(e) => handleChangeShortcut(e.target.value)}
                  label="Atajo"
                >
                  <MenuItem value="none">Sin atajo</MenuItem>
                  {datesShortCuts.map((sc) => (
                    <MenuItem key={sc.label} value={sc.label}>
                      {sc.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <div>
                <Typography variant="body2" marginBottom={1}>
                  {filters.find((f) => f.dateKey === 'startDate')?.label}
                </Typography>
                <div style={{ display: 'flex', gap: 8 }}>
                  <TextField
                    label="Fecha de inicio"
                    type="date"
                    size="small"
                    value={
                      filters.find((f) => f.dateKey === 'startDate')?.value ||
                      ''
                    }
                    onChange={(e) =>
                      handleChangeDateNormal(e.target.value, 'start')
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="Fecha de fin"
                    type="date"
                    size="small"
                    value={
                      filters.find((f) => f.dateKey === 'endDate')?.value || ''
                    }
                    onChange={(e) =>
                      handleChangeDateNormal(e.target.value, 'end')
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </div>
              </div>
            )}
          </div>
        )}
        <div style={{ marginBottom: 8 }}>
          <Typography variant="body1" fontWeight="bold" marginBottom={1}>
            <span style={{ fontSize: '1px' }}>_</span>
            Filtros
          </Typography>
          {filters
            .filter((filter) => filter.type !== 'date')
            .map((filter, index) => (
              <div key={index} style={{ padding: '5px 0px' }}>
                {filter.type === 'text' && (
                  <TextField
                    label={filter.label}
                    value={fakeValues[filter.label] || ''}
                    onChange={(e) =>
                      handleChangeText(
                        e.target.value,
                        filter.setter,
                        filter.label,
                        filter.specialHandler,
                      )
                    }
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                )}
                {filter.type === 'select' && filter.options && (
                  <FormControl fullWidth size="small">
                    <InputLabel>{filter.label}</InputLabel>
                    <Select
                      value={filter.value || ''}
                      onChange={(e) => filter.setter?.(e.target.value)}
                      label={filter.label}
                    >
                      {filter.options.map((option, idx) => (
                        <MenuItem key={idx} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {filter.type === 'multiple' && filter.options && (
                  <Autocomplete
                    multiple
                    value={filter.value || []}
                    onChange={(e, value) => filter.setter?.(value)}
                    options={filter.options}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={filter.label}
                        size="small"
                      />
                    )}
                  />
                )}
                {filter.type === 'autocomplete' && (
                  <Autocomplete
                    value={
                      autocompleteValues[filter.label] || filter.value || null
                    }
                    onChange={(e, value) =>
                      handleAutocompleteChange(filter.label, value)
                    }
                    inputValue={autocompleteInputValues[filter.label] || ''}
                    onInputChange={(e, newInputValue, reason) =>
                      handleAutocompleteInputChange(
                        filter.label,
                        newInputValue,
                        reason,
                      )
                    }
                    options={autocompleteOptions[filter.label] || []}
                    loading={autocompleteLoading[filter.label] || false}
                    loadingText="Cargando opciones..."
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.value === value?.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={filter.label}
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {autocompleteLoading[filter.label] ? (
                                <div>
                                  <CircularProgress size={20} />
                                </div>
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              </div>
            ))}
        </div>
        {sortOptions && (
          <div>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              Ordenar
            </Typography>
            <div style={{ padding: '5px 0px' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Por</InputLabel>
                <Select
                  value={selectedSortBy || ''}
                  onChange={(e) => setSelectedSortBy?.(e.target.value)}
                  label="Por"
                >
                  {sortOptions.map((option, idx) => (
                    <MenuItem key={idx} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div style={{ padding: '5px 0px' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Orden</InputLabel>
                <Select
                  value={sortOrder || ''}
                  onChange={(e) =>
                    setSortOrder?.(e.target.value as 'asc' | 'desc')
                  }
                  label="Orden"
                >
                  <MenuItem value="asc">Ascendente</MenuItem>
                  <MenuItem value="desc">Descendente</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        )}

        <div style={{ padding: '10px 0px', textAlign: 'right' }}>
          <CustomButton
            color="primary"
            onClick={() => handleSearch()}
            disabled={disableSearch}
          >
            Buscar
          </CustomButton>
        </div>
      </div>
    </MenuDialog>
  );
};

export default GlobalSearcher;
